import React, { useMemo, useState } from 'react';
import { GraphMember } from '@/contexts/FamilyGraphContext';
import { CheckCircle2, Circle, Sparkles, X } from 'lucide-react';

interface Props {
  members: GraphMember[];
  /** Has the current user already sent at least one invite? */
  invitesSent: number;
  onAddParent: () => void;
  onAddOther: () => void;
  onInvite: () => void;
}

/**
 * Post-signup checklist — surfaces a 3-step "do this next" guide for newly
 * signed-up users so they're never idle on the dashboard.
 *
 *   Step 1: Add a parent
 *   Step 2: Add another relative
 *   Step 3: Invite someone
 *
 * Auto-hides once all three are done OR the user has 3+ approved relatives
 * AND has sent at least one invite. Also dismissible — preference is held
 * in localStorage so it doesn't pop back on every page load.
 */
const PostSignupChecklist: React.FC<Props> = ({
  members,
  invitesSent,
  onAddParent,
  onAddOther,
  onInvite,
}) => {
  const STORAGE_KEY = 'kc:postSignupChecklist:dismissed';
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const { hasParent, otherRelativeCount, allDone } = useMemo(() => {
    const approved = members.filter(
      (m) =>
        m.approvalStatus !== 'rejected' &&
        m.side !== 'self' &&
        m.relationship !== 'unknown'
    );
    const hasParent = approved.some(
      (m) =>
        m.relationship === 'mother' ||
        m.relationship === 'father' ||
        m.relationship === 'step-parent' ||
        m.relationship === 'adopted-parent'
    );
    const otherCount = approved.filter(
      (m) =>
        m.relationship !== 'mother' &&
        m.relationship !== 'father' &&
        m.relationship !== 'step-parent' &&
        m.relationship !== 'adopted-parent'
    ).length;
    const sent = invitesSent >= 1;
    return {
      hasParent,
      otherRelativeCount: otherCount,
      allDone: hasParent && otherCount >= 1 && sent,
    };
  }, [members, invitesSent]);

  if (dismissed) return null;
  if (allDone) return null;

  const steps = [
    {
      id: 'parent',
      label: 'Add a parent',
      sub: 'Mother or father — the anchor of your tree.',
      done: hasParent,
      action: onAddParent,
    },
    {
      id: 'other',
      label: 'Add another relative',
      sub: 'A sibling, child, or grandparent — anyone close.',
      done: otherRelativeCount >= 1,
      action: onAddOther,
    },
    {
      id: 'invite',
      label: 'Invite someone',
      sub: 'A family member who can help fill in the tree.',
      done: invitesSent >= 1,
      action: onInvite,
    },
  ];

  const completed = steps.filter((s) => s.done).length;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50 border-2 border-fuchsia-200 p-5 relative">
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 transition"
        aria-label="Dismiss checklist"
        title="Hide this checklist"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-fuchsia-600" />
        <h3 className="font-serif font-bold text-lg text-slate-900">
          Get started in 3 steps
        </h3>
        <span className="ml-auto mr-7 text-xs font-bold text-fuchsia-700 bg-fuchsia-100 px-2 py-0.5 rounded-full">
          {completed}/{steps.length}
        </span>
      </div>
      <p className="text-xs text-slate-600 mb-3">
        Build the foundation of your circle — then everyone else can be added
        from these anchors.
      </p>
      <div className="space-y-2">
        {steps.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            onClick={s.done ? undefined : s.action}
            disabled={s.done}
            className={`w-full flex items-center gap-3 rounded-xl p-3 text-left transition ${
              s.done
                ? 'bg-emerald-50 border border-emerald-200 cursor-default'
                : 'bg-white border border-fuchsia-200 hover:border-fuchsia-400 hover:shadow-sm'
            }`}
          >
            {s.done ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <Circle className="w-5 h-5 text-fuchsia-400 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div
                className={`text-sm font-bold leading-tight ${
                  s.done ? 'text-emerald-800 line-through' : 'text-slate-900'
                }`}
              >
                Step {idx + 1}: {s.label}
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5 leading-tight">
                {s.sub}
              </div>
            </div>
            {!s.done && (
              <span className="shrink-0 text-xs text-fuchsia-700 font-bold">
                Start →
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PostSignupChecklist;
